import { createServerFn } from "@tanstack/react-start";

import {
  specGroupCreateInputSchema,
  specGroupDeleteInputSchema,
  specGroupMoveInputSchema,
  specGroupStatusInputSchema,
  specVersionFinalizeAllInputSchema,
  specGroupUpdateInputSchema,
  specLineClearInputSchema,
  specGroupResetInputSchema,
  specLineResetInputSchema,
  specSelectionResetInputSchema,
  specLineCreateInputSchema,
  specLineDeleteInputSchema,
  specLineDuplicateInputSchema,
  specLineLocationNotesInputSchema,
  specLineLocationsInputSchema,
  specLineLocationTypeInputSchema,
  specLineMoveInputSchema,
  specLinePerPlanInputSchema,
  specLineStatusInputSchema,
  specLineUpdateInputSchema,
  specLocationCreateInputSchema,
  specLocationDeleteInputSchema,
  specLocationRenameInputSchema,
  specSelectionClearItemInputSchema,
  specSelectionCreateInputSchema,
  specSelectionDeleteInputSchema,
  specSelectionItemInputSchema,
  specSelectionManufacturerInputSchema,
  specSelectionPaidUpgradeInputSchema,
  specSelectionUpdateInputSchema,
  specSummaryEditInputSchema,
  type SpecGroupCreateBrowserInput,
  type SpecGroupDeleteBrowserInput,
  type SpecGroupMoveBrowserInput,
  type SpecGroupStatusBrowserInput,
  type SpecVersionFinalizeAllBrowserInput,
  type SpecGroupUpdateBrowserInput,
  type SpecLineClearBrowserInput,
  type SpecGroupResetBrowserInput,
  type SpecLineResetBrowserInput,
  type SpecSelectionResetBrowserInput,
  type SpecLineCreateBrowserInput,
  type SpecLineDeleteBrowserInput,
  type SpecLineDuplicateBrowserInput,
  type SpecLineLocationNotesBrowserInput,
  type SpecLineLocationsBrowserInput,
  type SpecLineLocationTypeBrowserInput,
  type SpecLineMoveBrowserInput,
  type SpecLinePerPlanBrowserInput,
  type SpecLineStatusBrowserInput,
  type SpecLineUpdateBrowserInput,
  type SpecLocationCreateBrowserInput,
  type SpecLocationDeleteBrowserInput,
  type SpecLocationRenameBrowserInput,
  type SpecSelectionClearItemBrowserInput,
  type SpecSelectionCreateBrowserInput,
  type SpecSelectionDeleteBrowserInput,
  type SpecSelectionItemBrowserInput,
  type SpecSelectionManufacturerBrowserInput,
  type SpecSelectionPaidUpgradeBrowserInput,
  type SpecSelectionUpdateBrowserInput,
  type SpecSummaryEditBrowserInput,
} from "@/api/contracts/spec-editor";
import { assertCanEditSpecs } from "@/api/functions/admin-gate";
import { requireAuth } from "@/api/middleware/auth";
import type { ResolvedSession } from "@/server/use-cases/auth/resolve-session";

// The browser's doors onto the Editor's verbs: the five group verbs and the
// header pencil's write (#799), the six line verbs (#800), the seven
// selection verbs (#801), the six location verbs (#802) and the two per-plan
// verbs (#803); #804 joins them. Each
// answers with the shared `EditorMutationResult`, which the verb's hook lays
// onto the cache (`@/hooks/spec-editor-cache`).
//
// The gates are the lifecycle verbs' (`@/api/functions/specifications`):
// `requireAuth`, then the `canEditSpecs` capability (`assertCanEditSpecs`,
// shared from the admin gate) — a better error before the round trip — then
// Python's `require_any_role(SPEC_WRITE_ROLES)` and the use cases' own
// resolver, the enforcement (ADR-0011's scope is Python's alone). Error scrubbing is global function middleware
// (`start.ts`); server-only modules come in through a dynamic `import()`.
//
// The browser input names the spec beside the version, for the cache; the
// internal wire is addressed by the version alone, so the spec id stops here.

interface HandlerContext {
  session: ResolvedSession;
}

/** A verb's browser input without the spec id the wire does not carry. */
function forTheWire<T extends { specificationId: number }>({
  specificationId: _specificationId,
  ...rest
}: T) {
  return rest;
}

async function handleCreateGroup({
  data,
  context,
}: {
  data: SpecGroupCreateBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { createInternalSpecGroup } = await import("@/server/data/internal-api/spec-editor");
  return createInternalSpecGroup(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleUpdateGroup({
  data,
  context,
}: {
  data: SpecGroupUpdateBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { updateInternalSpecGroup } = await import("@/server/data/internal-api/spec-editor");
  return updateInternalSpecGroup(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleDeleteGroup({
  data,
  context,
}: {
  data: SpecGroupDeleteBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { deleteInternalSpecGroup } = await import("@/server/data/internal-api/spec-editor");
  return deleteInternalSpecGroup(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleUpdateGroupStatus({
  data,
  context,
}: {
  data: SpecGroupStatusBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { updateInternalSpecGroupStatus } = await import("@/server/data/internal-api/spec-editor");
  return updateInternalSpecGroupStatus(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleFinalizeAll({
  data,
  context,
}: {
  data: SpecVersionFinalizeAllBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { finalizeInternalSpecVersionLines } =
    await import("@/server/data/internal-api/spec-editor");
  return finalizeInternalSpecVersionLines(forTheWire(data), {
    sessionId: context.session.sessionId,
  });
}

async function handleMoveGroup({
  data,
  context,
}: {
  data: SpecGroupMoveBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { moveInternalSpecGroup } = await import("@/server/data/internal-api/spec-editor");
  return moveInternalSpecGroup(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleEditSummary({
  data,
  context,
}: {
  data: SpecSummaryEditBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { editInternalSpecSummary } = await import("@/server/data/internal-api/spec-editor");
  return editInternalSpecSummary(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleCreateLine({
  data,
  context,
}: {
  data: SpecLineCreateBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { createInternalSpecLine } = await import("@/server/data/internal-api/spec-editor");
  return createInternalSpecLine(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleUpdateLine({
  data,
  context,
}: {
  data: SpecLineUpdateBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { updateInternalSpecLine } = await import("@/server/data/internal-api/spec-editor");
  return updateInternalSpecLine(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleUpdateLineStatus({
  data,
  context,
}: {
  data: SpecLineStatusBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { updateInternalSpecLineStatus } = await import("@/server/data/internal-api/spec-editor");
  return updateInternalSpecLineStatus(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleDeleteLine({
  data,
  context,
}: {
  data: SpecLineDeleteBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { deleteInternalSpecLine } = await import("@/server/data/internal-api/spec-editor");
  return deleteInternalSpecLine(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleMoveLine({
  data,
  context,
}: {
  data: SpecLineMoveBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { moveInternalSpecLine } = await import("@/server/data/internal-api/spec-editor");
  return moveInternalSpecLine(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleDuplicateLine({
  data,
  context,
}: {
  data: SpecLineDuplicateBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { duplicateInternalSpecLine } = await import("@/server/data/internal-api/spec-editor");
  return duplicateInternalSpecLine(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleClearLine({
  data,
  context,
}: {
  data: SpecLineClearBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { clearInternalSpecLine } = await import("@/server/data/internal-api/spec-editor");
  return clearInternalSpecLine(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleResetLine({
  data,
  context,
}: {
  data: SpecLineResetBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { resetInternalSpecLine } = await import("@/server/data/internal-api/spec-editor");
  return resetInternalSpecLine(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleResetSelection({
  data,
  context,
}: {
  data: SpecSelectionResetBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { resetInternalSpecSelection } = await import("@/server/data/internal-api/spec-editor");
  return resetInternalSpecSelection(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleResetGroup({
  data,
  context,
}: {
  data: SpecGroupResetBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { resetInternalSpecGroup } = await import("@/server/data/internal-api/spec-editor");
  return resetInternalSpecGroup(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleCreateSelection({
  data,
  context,
}: {
  data: SpecSelectionCreateBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { createInternalSpecSelection } = await import("@/server/data/internal-api/spec-editor");
  return createInternalSpecSelection(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleUpdateSelection({
  data,
  context,
}: {
  data: SpecSelectionUpdateBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { updateInternalSpecSelection } = await import("@/server/data/internal-api/spec-editor");
  return updateInternalSpecSelection(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleDeleteSelection({
  data,
  context,
}: {
  data: SpecSelectionDeleteBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { deleteInternalSpecSelection } = await import("@/server/data/internal-api/spec-editor");
  return deleteInternalSpecSelection(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleSetSelectionItem({
  data,
  context,
}: {
  data: SpecSelectionItemBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { setInternalSpecSelectionItem } = await import("@/server/data/internal-api/spec-editor");
  return setInternalSpecSelectionItem(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleClearSelectionItem({
  data,
  context,
}: {
  data: SpecSelectionClearItemBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { clearInternalSpecSelectionItem } = await import("@/server/data/internal-api/spec-editor");
  return clearInternalSpecSelectionItem(forTheWire(data), {
    sessionId: context.session.sessionId,
  });
}

async function handleUpdateSelectionPaidUpgrade({
  data,
  context,
}: {
  data: SpecSelectionPaidUpgradeBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { updateInternalSpecSelectionPaidUpgrade } =
    await import("@/server/data/internal-api/spec-editor");
  return updateInternalSpecSelectionPaidUpgrade(forTheWire(data), {
    sessionId: context.session.sessionId,
  });
}

async function handleUpdateLineLocations({
  data,
  context,
}: {
  data: SpecLineLocationsBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { updateInternalSpecLineLocations } =
    await import("@/server/data/internal-api/spec-editor");
  return updateInternalSpecLineLocations(forTheWire(data), {
    sessionId: context.session.sessionId,
  });
}

async function handleCreateLocation({
  data,
  context,
}: {
  data: SpecLocationCreateBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { createInternalSpecLocation } = await import("@/server/data/internal-api/spec-editor");
  return createInternalSpecLocation(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleUpdateLineLocationType({
  data,
  context,
}: {
  data: SpecLineLocationTypeBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { updateInternalSpecLineLocationType } =
    await import("@/server/data/internal-api/spec-editor");
  return updateInternalSpecLineLocationType(forTheWire(data), {
    sessionId: context.session.sessionId,
  });
}

async function handleUpdateLineLocationNotes({
  data,
  context,
}: {
  data: SpecLineLocationNotesBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { updateInternalSpecLineLocationNotes } =
    await import("@/server/data/internal-api/spec-editor");
  return updateInternalSpecLineLocationNotes(forTheWire(data), {
    sessionId: context.session.sessionId,
  });
}

async function handleRenameLocation({
  data,
  context,
}: {
  data: SpecLocationRenameBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { renameInternalSpecLocation } = await import("@/server/data/internal-api/spec-editor");
  return renameInternalSpecLocation(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleDeleteLocation({
  data,
  context,
}: {
  data: SpecLocationDeleteBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { deleteInternalSpecLocation } = await import("@/server/data/internal-api/spec-editor");
  return deleteInternalSpecLocation(forTheWire(data), { sessionId: context.session.sessionId });
}

async function handleUpdateLinePerPlan({
  data,
  context,
}: {
  data: SpecLinePerPlanBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { updateInternalSpecLinePerPlan } = await import("@/server/data/internal-api/spec-editor");
  return updateInternalSpecLinePerPlan(forTheWire(data), {
    sessionId: context.session.sessionId,
  });
}

async function handleUpdateSelectionManufacturer({
  data,
  context,
}: {
  data: SpecSelectionManufacturerBrowserInput;
  context: HandlerContext;
}) {
  assertCanEditSpecs(context.session);
  const { updateInternalSpecSelectionManufacturer } =
    await import("@/server/data/internal-api/spec-editor");
  return updateInternalSpecSelectionManufacturer(forTheWire(data), {
    sessionId: context.session.sessionId,
  });
}

/**
 * The handler bodies, exported for unit tests: a `createServerFn` value is not
 * callable as a plain function outside the Start runtime.
 */
export const __test_handlers = {
  handleCreateGroup,
  handleUpdateGroup,
  handleDeleteGroup,
  handleUpdateGroupStatus,
  handleFinalizeAll,
  handleMoveGroup,
  handleEditSummary,
  handleCreateLine,
  handleUpdateLine,
  handleUpdateLineStatus,
  handleDeleteLine,
  handleMoveLine,
  handleDuplicateLine,
  handleClearLine,
  handleResetLine,
  handleResetSelection,
  handleResetGroup,
  handleCreateSelection,
  handleUpdateSelection,
  handleDeleteSelection,
  handleSetSelectionItem,
  handleClearSelectionItem,
  handleUpdateSelectionPaidUpgrade,
  handleUpdateLineLocations,
  handleCreateLocation,
  handleUpdateLineLocationType,
  handleUpdateLineLocationNotes,
  handleRenameLocation,
  handleDeleteLocation,
  handleUpdateLinePerPlan,
  handleUpdateSelectionManufacturer,
};

export const specGroupCreate = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specGroupCreateInputSchema)
  .handler(handleCreateGroup);

export const specGroupUpdate = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specGroupUpdateInputSchema)
  .handler(handleUpdateGroup);

export const specGroupDelete = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specGroupDeleteInputSchema)
  .handler(handleDeleteGroup);

export const specGroupUpdateStatus = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specGroupStatusInputSchema)
  .handler(handleUpdateGroupStatus);

/** Mark all as finalized (#950) — the Review & Publish dialog's step 1. */
export const specVersionFinalizeAll = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specVersionFinalizeAllInputSchema)
  .handler(handleFinalizeAll);

export const specGroupMove = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specGroupMoveInputSchema)
  .handler(handleMoveGroup);

export const specSummaryEdit = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specSummaryEditInputSchema)
  .handler(handleEditSummary);

export const specLineCreate = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specLineCreateInputSchema)
  .handler(handleCreateLine);

export const specLineUpdate = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specLineUpdateInputSchema)
  .handler(handleUpdateLine);

export const specLineUpdateStatus = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specLineStatusInputSchema)
  .handler(handleUpdateLineStatus);

export const specLineDelete = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specLineDeleteInputSchema)
  .handler(handleDeleteLine);

export const specLineMove = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specLineMoveInputSchema)
  .handler(handleMoveLine);

export const specLineDuplicate = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specLineDuplicateInputSchema)
  .handler(handleDuplicateLine);

export const specLineClear = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specLineClearInputSchema)
  .handler(handleClearLine);

/** Reset to Base / to Core on a line (#1084). */
export const specLineReset = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specLineResetInputSchema)
  .handler(handleResetLine);

/** Reset to Base / to Core on a selection row (#1084). */
export const specSelectionReset = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specSelectionResetInputSchema)
  .handler(handleResetSelection);

/** Reset group name and notes to Base / to Core (#1084). */
export const specGroupReset = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specGroupResetInputSchema)
  .handler(handleResetGroup);

export const specSelectionCreate = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specSelectionCreateInputSchema)
  .handler(handleCreateSelection);

export const specSelectionUpdate = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specSelectionUpdateInputSchema)
  .handler(handleUpdateSelection);

export const specSelectionDelete = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specSelectionDeleteInputSchema)
  .handler(handleDeleteSelection);

export const specSelectionSetItem = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specSelectionItemInputSchema)
  .handler(handleSetSelectionItem);

export const specSelectionClearItem = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specSelectionClearItemInputSchema)
  .handler(handleClearSelectionItem);

export const specSelectionUpdatePaidUpgrade = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specSelectionPaidUpgradeInputSchema)
  .handler(handleUpdateSelectionPaidUpgrade);

export const specLineUpdateLocations = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specLineLocationsInputSchema)
  .handler(handleUpdateLineLocations);

export const specLocationCreate = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specLocationCreateInputSchema)
  .handler(handleCreateLocation);

export const specLineUpdateLocationType = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specLineLocationTypeInputSchema)
  .handler(handleUpdateLineLocationType);

export const specLineUpdateLocationNotes = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specLineLocationNotesInputSchema)
  .handler(handleUpdateLineLocationNotes);

export const specLocationRename = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specLocationRenameInputSchema)
  .handler(handleRenameLocation);

export const specLocationDelete = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specLocationDeleteInputSchema)
  .handler(handleDeleteLocation);

export const specLineUpdatePerPlan = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specLinePerPlanInputSchema)
  .handler(handleUpdateLinePerPlan);

export const specSelectionUpdateManufacturer = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(specSelectionManufacturerInputSchema)
  .handler(handleUpdateSelectionManufacturer);
